import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Save, X, Search } from "lucide-react";

interface WorkspaceUsageRow {
  workspace: { id: string; companyName: string };
  quota: { budgetCents: number; warningThreshold: number } | null;
  usedCents: number;
}

interface AiUsageResponse {
  data: WorkspaceUsageRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 10;

function usageStatusColor(percent: number) {
  if (percent > 80) return { bg: "bg-red-100", text: "text-red-800", dot: "bg-red-500" };
  if (percent >= 60) return { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" };
  return { bg: "bg-green-100", text: "text-green-800", dot: "bg-green-500" };
}

async function fetchAiUsage(page: number, search: string): Promise<AiUsageResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: PAGE_SIZE.toString(),
    search,
  });
  const res = await fetch(`/api/admin/ai-usage?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch");
  return res.json();
}

export default function AdminAiUsage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ai-usage", page, debouncedSearch],
    queryFn: () => fetchAiUsage(page, debouncedSearch),
  });

  const saveBudget = useMutation({
    mutationFn: async ({
      workspaceId,
      budgetCents,
    }: {
      workspaceId: string;
      budgetCents: number;
    }) => {
      const res = await fetch(`/api/admin/workspaces/${workspaceId}/ai-quota`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ budgetCents }),
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: (_, { workspaceId }) => {
      qc.invalidateQueries({ queryKey: ["admin-ai-usage"] });
      setEditing((prev) => {
        const next = { ...prev };
        delete next[workspaceId];
        return next;
      });
      toast({ title: "Budget updated" });
    },
    onError: () => toast({ title: "Failed to update budget", variant: "destructive" }),
  });

  const columns: ColumnDef<WorkspaceUsageRow>[] = [
    {
      id: "workspace",
      header: "Workspace",
      cell: ({ workspace }) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-primary">
              {workspace.companyName.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <span className="text-sm font-bold text-foreground">{workspace.companyName}</span>
        </div>
      ),
    },
    {
      id: "budget",
      header: "Budget ($)",
      cell: ({ workspace, quota }) => {
        const budgetCents = quota?.budgetCents ?? 0;
        const isEditingRow = workspace.id in editing;
        return isEditingRow ? (
          <Input
            className="w-24 h-8 text-xs rounded-2xl"
            type="number"
            min={0}
            step={0.01}
            value={editing[workspace.id]}
            onChange={(e) => setEditing((prev) => ({ ...prev, [workspace.id]: e.target.value }))}
          />
        ) : (
          <span
            className="text-sm text-foreground cursor-pointer hover:underline"
            onClick={() =>
              setEditing((prev) => ({ ...prev, [workspace.id]: String(budgetCents / 100) }))
            }
          >
            ${(budgetCents / 100).toFixed(2)}
          </span>
        );
      },
    },
    {
      id: "used",
      header: "Used ($)",
      cell: ({ usedCents }) => <span className="text-sm text-foreground">${(usedCents / 100).toFixed(4)}</span>,
    },
    {
      id: "percentUsed",
      header: "% Used",
      cell: ({ quota, usedCents }) => {
        const budgetCents = quota?.budgetCents ?? 0;
        const percent = budgetCents > 0 ? Math.round((usedCents / budgetCents) * 100) : 0;
        const status = usageStatusColor(percent);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {percent}%
          </span>
        );
      },
    },
    {
      id: "remaining",
      header: "Remaining",
      cell: ({ quota, usedCents }) => {
        const budgetCents = quota?.budgetCents ?? 0;
        const remaining = Math.max(0, budgetCents - usedCents);
        return <span className="text-sm text-foreground">${(remaining / 100).toFixed(2)}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: ({ workspace, quota }) => {
        const budgetCents = quota?.budgetCents ?? 0;
        const isEditingRow = workspace.id in editing;
        return (
          <div className="flex justify-end gap-1">
            {isEditingRow ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const val = parseFloat(editing[workspace.id]);
                    if (!isNaN(val) && val >= 0) {
                      saveBudget.mutate({
                        workspaceId: workspace.id,
                        budgetCents: Math.round(val * 100),
                      });
                    }
                  }}
                  disabled={saveBudget.isPending}
                  title="Save"
                >
                  <Save className="h-4 w-4 text-primary" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() =>
                    setEditing((prev) => {
                      const next = { ...prev };
                      delete next[workspace.id];
                      return next;
                    })
                  }
                  title="Cancel"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() =>
                  setEditing((prev) => ({ ...prev, [workspace.id]: String(budgetCents / 100) }))
                }
                title="Edit budget"
              >
                <Pencil className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-black tracking-tight text-primary">AI Usage</h1>
        <p className="text-lg text-muted-foreground">Monitor and manage workspace AI quotas</p>
      </div>

      {/* Workspace Quotas Card */}
      <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-6 border-b border-border/30 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-foreground">Workspace Quotas</h3>
            <p className="text-sm text-muted-foreground">Set and track AI spending limits per workspace</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9 w-56 rounded-2xl"
              placeholder="Search workspaces..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(row) => row.workspace.id}
            isLoading={isLoading}
            emptyState={{ title: "No workspaces found." }}
            pagination={
              data
                ? {
                    page,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
